import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { DataService } from '../../Service/data.service';

export type EveeRole = 'admin' | 'subscriber' | 'admin-as-subscriber';

export interface EveeMessage {
  from: 'evee' | 'user';
  text: string;
}

export interface EveeCommand {
  id: string;
  phrases: string[];
  roles: EveeRole[];
  kind: 'navigate' | 'order-report' | 'qa-release' | 'switch-subscriber' | 'switch-admin' | 'logout' | 'help' | 'click';
  path?: string;
  clickText?: string;
  reply: string;
}

export interface EveePending {
  kind: 'order-report' | 'navigate' | 'qa-release' | 'click';
  path?: string;
  query?: string;
  clickText?: string;
  speak?: string;
}

const PENDING_KEY = 'eveePending';
const GREETING = 'Hello, I am your personal PredictScreen assistant, Evee. How can I help you?';
const VOICE_PITCH = 1.18;
const VOICE_RATE = 0.95;

@Injectable({ providedIn: 'root' })
export class EveeAssistantService {
  readonly greeting = GREETING;
  readonly name = 'Evee';

  messages: EveeMessage[] = [];
  listening = false;
  speaking = false;
  subscriberChoices: any[] = [];
  awaitingSubscriber = false;
  subscriberQuery = '';
  pendingAfterSubscriber: EveePending = null;
  confirmAction: string = null;

  private recognition: any;
  private onUpdate: () => void;
  private chosenVoice: any;

  readonly commands: EveeCommand[] = [
    { id: 'help', phrases: ['help', 'what can you do', 'commands', 'what can i say'], roles: ['admin', 'subscriber', 'admin-as-subscriber'], kind: 'help', reply: '' },
    { id: 'home', phrases: ['home', 'go home', 'dashboard', 'open home'], roles: ['admin', 'subscriber', 'admin-as-subscriber'], kind: 'navigate', path: '/home', reply: 'Opening Home.' },
    { id: 'order-report', phrases: ['order a report', 'order report', 'new report', 'order screening', 'place an order', 'order reports'], roles: ['admin', 'subscriber', 'admin-as-subscriber'], kind: 'order-report', path: '/subscriber-reports/order-reports', reply: 'Opening the order report form.' },
    { id: 'report-list', phrases: ['report list', 'order report list', 'my reports', 'view reports', 'show reports'], roles: ['subscriber', 'admin-as-subscriber'], kind: 'navigate', path: '/subscriber-reports/report-list', reply: 'Opening Order Reports.' },
    { id: 'analytics', phrases: ['analytics report', 'analytics'], roles: ['subscriber', 'admin-as-subscriber'], kind: 'navigate', path: '/subscriber-reports/analytics-report', reply: 'Opening Analytics Report.' },
    { id: 'applicant-report', phrases: ['applicant report'], roles: ['subscriber', 'admin-as-subscriber'], kind: 'navigate', path: '/subscriber-reports/applicant-report', reply: 'Opening Applicant Report.' },
    { id: 'properties', phrases: ['properties', 'manage properties', 'open properties'], roles: ['subscriber', 'admin-as-subscriber'], kind: 'navigate', path: '/manageProperties', reply: 'Opening Properties.' },
    { id: 'sub-users', phrases: ['manage users', 'users', 'subscriber users'], roles: ['subscriber', 'admin-as-subscriber'], kind: 'navigate', path: '/subscriber_users/managesubscriberuser', reply: 'Opening Manage Users.' },
    { id: 'sub-roles', phrases: ['roles', 'permissions', 'manage roles'], roles: ['subscriber', 'admin-as-subscriber'], kind: 'navigate', path: '/subscriber_users/manageRolesAndPermissions', reply: 'Opening Roles and Permissions.' },
    { id: 'sub-email', phrases: ['email settings'], roles: ['subscriber', 'admin-as-subscriber'], kind: 'navigate', path: '/subscriber-settings/email-settings', reply: 'Opening Email Settings.' },
    { id: 'sub-feedback', phrases: ['tenant feedback setting', 'feedback settings'], roles: ['subscriber', 'admin-as-subscriber'], kind: 'navigate', path: '/tenant-feedback-data', reply: 'Opening Tenant Feedback Settings.' },
    { id: 'sub-wallet', phrases: ['manage wallet', 'wallet', 'manage payment'], roles: ['subscriber', 'admin-as-subscriber'], kind: 'navigate', path: '/subscriber-manage-payment', reply: 'Opening Manage Wallet.' },
    { id: 'sub-invoice', phrases: ['manage invoice', 'invoices'], roles: ['subscriber', 'admin-as-subscriber'], kind: 'navigate', path: '/subscriber-manage-invoice', reply: 'Opening Manage Invoice.' },
    { id: 'tenant-mgmt', phrases: ['tenant management', 'tenants'], roles: ['subscriber', 'admin-as-subscriber'], kind: 'navigate', path: '/tenant-management', reply: 'Opening Tenant Management.' },
    { id: 'online-app', phrases: ['view application', 'online application'], roles: ['subscriber', 'admin-as-subscriber'], kind: 'navigate', path: '/online-application', reply: 'Opening View Application.' },
    { id: 'criteria', phrases: ['predictscreen criteria', 'screening criteria'], roles: ['subscriber', 'admin-as-subscriber'], kind: 'navigate', path: '/admin-settings/screening', reply: 'Opening PredictScreen Criteria.' },
    { id: 'sub-platform', phrases: ['platform report'], roles: ['subscriber', 'admin-as-subscriber'], kind: 'navigate', path: '/subscriber-platform-report', reply: 'Opening Platform Report.' },
    { id: 'sub-profile', phrases: ['edit profile', 'my profile'], roles: ['subscriber', 'admin-as-subscriber'], kind: 'navigate', path: '/subscriber_edit_profile', reply: 'Opening your profile.' },
    { id: 'sub-2fa', phrases: ['account details', 'two factor', '2fa'], roles: ['subscriber', 'admin-as-subscriber'], kind: 'navigate', path: '/SubscribertwoFaAuth', reply: 'Opening Account Details.' },
    { id: 'sub-alerts', phrases: ['alert notification', 'notifications', 'alerts'], roles: ['subscriber', 'admin-as-subscriber'], kind: 'navigate', path: '/alert-notification', reply: 'Opening Alert Notifications.' },

    { id: 'qa', phrases: ['quality assurance', 'open qa', 'go to qa', 'qa queue'], roles: ['admin'], kind: 'navigate', path: '/qa', reply: 'Opening Quality Assurance.' },
    { id: 'qa-completed', phrases: ['completed reports', 'completed queue'], roles: ['admin'], kind: 'navigate', path: '/qa/completed', reply: 'Opening Completed Reports.' },
    { id: 'qa-release', phrases: ['release a report', 'release report', 'release from qa', 'release qa'], roles: ['admin'], kind: 'qa-release', path: '/qa', reply: 'Opening Quality Assurance to release a report.' },
    { id: 'vendor-orders', phrases: ['vendor orders', 'vendor queue'], roles: ['admin'], kind: 'navigate', path: '/vendor-orders/queue', reply: 'Opening Vendor Orders.' },
    { id: 'api-monitor', phrases: ['api monitor'], roles: ['admin'], kind: 'navigate', path: '/api-monitor', reply: 'Opening API Monitor.' },
    { id: 'new-signup', phrases: ['new signup', 'signup request', 'new request'], roles: ['admin'], kind: 'navigate', path: '/subscribers/new-request', reply: 'Opening New Signup Request.' },
    { id: 'manage-sub', phrases: ['manage subscriber', 'subscribers', 'subscriber list'], roles: ['admin'], kind: 'navigate', path: '/subscribers/manage-subscriber', reply: 'Opening Manage Subscriber.' },
    { id: 'admin-properties', phrases: ['manage properties', 'subscriber properties'], roles: ['admin'], kind: 'navigate', path: '/subscribers/manage-properties', reply: 'Opening Manage Properties.' },
    { id: 'archived', phrases: ['archived signup', 'archived request'], roles: ['admin'], kind: 'navigate', path: '/subscribers/archived-request', reply: 'Opening Archived Signup.' },
    { id: 'add-subscriber', phrases: ['add subscriber', 'create subscriber', 'new subscriber'], roles: ['admin'], kind: 'navigate', path: '/add-subscriber', reply: 'Opening Add Subscriber.' },
    { id: 'admin-users', phrases: ['manage users', 'admin users'], roles: ['admin'], kind: 'navigate', path: '/users/manageusers', reply: 'Opening Manage Users.' },
    { id: 'admin-roles', phrases: ['roles', 'permissions', 'manage roles'], roles: ['admin'], kind: 'navigate', path: '/users/manageRolesAndPermissions', reply: 'Opening Roles and Permissions.' },
    { id: 'sales-rep', phrases: ['sales representative', 'sales list'], roles: ['admin'], kind: 'navigate', path: '/users/salesrepresentative', reply: 'Opening Sales Representative List.' },
    { id: 'invoice', phrases: ['manage invoice', 'invoices'], roles: ['admin'], kind: 'navigate', path: '/manage-payments/invoice', reply: 'Opening Manage Invoice.' },
    { id: 'dv-invoice', phrases: ['dv invoice'], roles: ['admin'], kind: 'navigate', path: '/manage-payments/dvInvoice', reply: 'Opening DV Invoice.' },
    { id: 'wallet', phrases: ['manage wallet', 'wallet'], roles: ['admin'], kind: 'navigate', path: '/manage-payments/manageWallet', reply: 'Opening Manage Wallet.' },
    { id: 'credit', phrases: ['credit limit', 'manage credit'], roles: ['admin'], kind: 'navigate', path: '/manage-payments/credit', reply: 'Opening Manage Credit Limit.' },
    { id: 'waive', phrases: ['waive off', 'waive transactions'], roles: ['admin'], kind: 'navigate', path: '/manage-payments/waiveOffTransactions', reply: 'Opening Waive-Off Transactions.' },
    { id: 'tax-summary', phrases: ['tax summary'], roles: ['admin'], kind: 'navigate', path: '/manage-payments/taxSummaryReport', reply: 'Opening Tax Summary Report.' },
    { id: 'eviction-data', phrases: ['eviction data'], roles: ['admin'], kind: 'navigate', path: '/eviction-data', reply: 'Opening Eviction Data.' },
    { id: 'alias-eviction', phrases: ['eviction disposition', 'alias eviction'], roles: ['admin'], kind: 'navigate', path: '/manage-alias-setting/evictionData', reply: 'Opening Eviction Disposition.' },
    { id: 'alias-debt', phrases: ['debt categories', 'collections alias'], roles: ['admin'], kind: 'navigate', path: '/manage-alias-setting/collections', reply: 'Opening Debt Categories.' },
    { id: 'alias-criminal', phrases: ['criminal disposition'], roles: ['admin'], kind: 'navigate', path: '/manage-alias-setting/criminal', reply: 'Opening Criminal Disposition.' },
    { id: 'disclosure', phrases: ['disclosure management'], roles: ['admin'], kind: 'navigate', path: '/disclosureManagment', reply: 'Opening Disclosure Management.' },
    { id: 'logs', phrases: ['logs'], roles: ['admin'], kind: 'navigate', path: '/logs', reply: 'Opening Logs.' },
    { id: 'report-mgmt', phrases: ['report management'], roles: ['admin'], kind: 'navigate', path: '/report-management', reply: 'Opening Report Management.' },
    { id: 'dv-sub', phrases: ['dv subscriber', 'docuverus subscriber'], roles: ['admin'], kind: 'navigate', path: '/dv-integration/dv-subscriber', reply: 'Opening DV Subscriber.' },
    { id: 'dv-prop', phrases: ['dv properties', 'docuverus properties'], roles: ['admin'], kind: 'navigate', path: '/dv-integration/dv-properties', reply: 'Opening DV Properties.' },
    { id: 'dv-users', phrases: ['dv user', 'dv users'], roles: ['admin'], kind: 'navigate', path: '/dv-integration/dv-users', reply: 'Opening DV Users.' },
    { id: 'tenant-feedback', phrases: ['tenant feedback'], roles: ['admin'], kind: 'navigate', path: '/tenantFeedback', reply: 'Opening Tenant Feedback.' },
    { id: 'marketing-q', phrases: ['marketing queue', 'marketing'], roles: ['admin'], kind: 'navigate', path: '/marketing/marketing-queue', reply: 'Opening Marketing Queue.' },
    { id: 'marketing-r', phrases: ['marketing report'], roles: ['admin'], kind: 'navigate', path: '/marketing/reportList', reply: 'Opening Marketing Report.' },
    { id: 'dnd', phrases: ['dnd list', 'do not disturb'], roles: ['admin'], kind: 'navigate', path: '/marketing/dndList', reply: 'Opening DND List.' },
    { id: 'alerts', phrases: ['alerts', 'announcements'], roles: ['admin'], kind: 'navigate', path: '/alert-announcements', reply: 'Opening Alerts and Announcements.' },
    { id: 'offer-codes', phrases: ['offer codes'], roles: ['admin'], kind: 'navigate', path: '/admin-settings/offer-codes', reply: 'Opening Offer Codes.' },
    { id: 'combos', phrases: ['product combos', 'combos'], roles: ['admin'], kind: 'navigate', path: '/admin-settings/combos', reply: 'Opening Product Combos.' },
    { id: 'admin-email', phrases: ['email settings'], roles: ['admin'], kind: 'navigate', path: '/admin-settings/email-settings', reply: 'Opening Email Settings.' },
    { id: 'adverse', phrases: ['adverse letters'], roles: ['admin'], kind: 'navigate', path: '/admin-settings/adverseLetters', reply: 'Opening Adverse Letters.' },
    { id: 'screening', phrases: ['predictscreen criteria', 'screening criteria', 'screening'], roles: ['admin'], kind: 'navigate', path: '/admin-settings/screening', reply: 'Opening PredictScreen Criteria.' },
    { id: 'pricing', phrases: ['pricing'], roles: ['admin'], kind: 'navigate', path: '/admin-settings/pricing', reply: 'Opening Pricing.' },
    { id: 'processing', phrases: ['processing rules'], roles: ['admin'], kind: 'navigate', path: '/admin-settings/processing-rule', reply: 'Opening Processing Rules.' },
    { id: 'data-mgmt', phrases: ['data management'], roles: ['admin'], kind: 'navigate', path: '/admin-settings/data-managment', reply: 'Opening Data Management.' },
    { id: 'website', phrases: ['website management'], roles: ['admin'], kind: 'navigate', path: '/admin-settings/website-managment', reply: 'Opening Website Management.' },
    { id: 'tax-mgmt', phrases: ['tax management'], roles: ['admin'], kind: 'navigate', path: '/admin-settings/tax-managment', reply: 'Opening Tax Management.' },
    { id: 'vendor-settings', phrases: ['vendor settings'], roles: ['admin'], kind: 'navigate', path: '/admin-settings/vendor-settings', reply: 'Opening Vendor Settings.' },
    { id: 'ack', phrases: ['acknowledgement', 'document compliance'], roles: ['admin'], kind: 'navigate', path: '/document-compliance', reply: 'Opening Acknowledgement Message.' },
    { id: 'admin-platform', phrases: ['platform report'], roles: ['admin'], kind: 'navigate', path: '/platform-report', reply: 'Opening Platform Report.' },
    { id: 'admin-profile', phrases: ['edit profile', 'global search'], roles: ['admin'], kind: 'navigate', path: '/edit_profile', reply: 'Opening profile / global search.' },
    { id: 'admin-2fa', phrases: ['account details', 'two factor', '2fa'], roles: ['admin'], kind: 'navigate', path: '/twoFaAuth', reply: 'Opening Account Details.' },
    { id: 'inspection', phrases: ['inspection form'], roles: ['admin'], kind: 'navigate', path: '/inspectionFrom', reply: 'Opening Inspection Form.' },
    { id: 'inactive', phrases: ['inactive report'], roles: ['admin'], kind: 'navigate', path: '/inactiveReport', reply: 'Opening Inactive Report.' },
    { id: 'add-credential', phrases: ['add credential', 'credentials'], roles: ['admin'], kind: 'navigate', path: '/add-credential', reply: 'Opening Add Credential.' },
    { id: 'edit-screening', phrases: ['edit screening'], roles: ['admin'], kind: 'navigate', path: '/edit-screening', reply: 'Opening Edit Screening.' },
    { id: 'edit-pricing', phrases: ['edit pricing'], roles: ['admin'], kind: 'navigate', path: '/edit-pricing', reply: 'Opening Edit Pricing.' },
    { id: 'eviction-details', phrases: ['eviction details'], roles: ['admin'], kind: 'navigate', path: '/eviction-details', reply: 'Opening Eviction Details.' },
    { id: 'request-report', phrases: ['request report', 'consumer request report'], roles: ['admin'], kind: 'navigate', path: '/consumer-disclosure/request-report', reply: 'Opening Request Report.' },
    { id: 'request-grievance', phrases: ['request grievance', 'grievance'], roles: ['admin'], kind: 'navigate', path: '/consumer-disclosure/request-grievance', reply: 'Opening Request Grievance.' },
    { id: 'tenant-email', phrases: ['tenant email'], roles: ['admin'], kind: 'navigate', path: '/tenantEmail', reply: 'Opening Tenant Email.' },
    { id: 'switch-sub', phrases: ['switch subscriber', 'change subscriber', 'pick subscriber'], roles: ['admin', 'admin-as-subscriber'], kind: 'switch-subscriber', reply: 'Which subscriber should I switch to?' },
    { id: 'switch-admin', phrases: ['switch to admin', 'back to admin', 'leave subscriber'], roles: ['admin-as-subscriber'], kind: 'switch-admin', reply: 'Switching back to admin.' },
    { id: 'logout', phrases: ['logout', 'log out', 'sign out'], roles: ['admin', 'subscriber', 'admin-as-subscriber'], kind: 'logout', reply: 'Say yes to log out.' }
  ];

  constructor(
    private router: Router,
    private dataService: DataService,
    private zone: NgZone
  ) {
    this.primeVoices();
  }

  bind(onUpdate: () => void) {
    this.onUpdate = onUpdate;
  }

  getRole(): EveeRole {
    const subscriberLogin = this.readSession('subscriberLogin');
    const userLogin = this.readSession('userLogin');
    if (subscriberLogin && subscriberLogin.Data && subscriberLogin.Data.role === 'admin') {
      return 'admin-as-subscriber';
    }
    if (subscriberLogin) {
      return 'subscriber';
    }
    if (userLogin && userLogin.tableName === 'txnAdminUsers') {
      return 'admin';
    }
    return 'subscriber';
  }

  isAssistantUser(): boolean {
    const subscriberLogin = this.readSession('subscriberLogin');
    const userLogin = this.readSession('userLogin');
    return !!(subscriberLogin || (userLogin && userLogin.tableName === 'txnAdminUsers'));
  }

  roleCommands(): EveeCommand[] {
    const role = this.getRole();
    return this.commands.filter(cmd => cmd.roles.indexOf(role) > -1);
  }

  say(text: string) {
    this.messages.push({ from: 'evee', text });
    this.speak(text);
    this.ping();
  }

  greet() {
    this.say(GREETING);
  }

  handleTranscript(raw: string) {
    const spoken = (raw || '').trim();
    if (!spoken) {
      return;
    }
    this.messages.push({ from: 'user', text: spoken });
    this.ping();
    this.runIntent(spoken);
  }

  runIntent(raw: string) {
    const text = this.normalize(raw);
    if (!text) {
      this.say('I did not catch that. Please try again.');
      return;
    }

    if (this.confirmAction) {
      this.resolveConfirm(text);
      return;
    }

    if (this.awaitingSubscriber) {
      this.resolveSubscriberChoice(raw);
      return;
    }

    const forMatch = text.match(/^(?:order(?: a)? report|order screening|switch subscriber|switch to|release(?: a)? report(?: from qa)?)(?: for)? (.+)$/);
    const named = forMatch ? forMatch[1].trim() : '';

    if (this.isOrderIntent(text)) {
      this.startOrderReport(named);
      return;
    }

    if (this.isReleaseIntent(text)) {
      this.startQaRelease(named);
      return;
    }

    if (/^switch (to )?subscriber/.test(text) || /^switch to /.test(text)) {
      const name = named || text.replace(/^switch (to )?(subscriber )?/, '').trim();
      this.startSwitchSubscriber(name);
      return;
    }

    const searchMatch = text.match(/^(?:search|find|look up|look for)\s+(.+)$/);
    if (searchMatch) {
      this.fillSearch(searchMatch[1]);
      this.say('Searching for ' + searchMatch[1] + '.');
      return;
    }

    const clickMatch = text.match(/^(?:click|press|tap|open|select)\s+(.+)$/);
    if (clickMatch) {
      this.clickByText(clickMatch[1]);
      return;
    }

    const match = this.matchCommand(text);
    if (match) {
      this.executeCommand(match);
      return;
    }

    if (this.clickByText(text, true)) {
      return;
    }

    this.say('I can open pages, click on-screen buttons, search, order a report, or release a report from QA. Try saying help.');
  }

  executeCommand(cmd: EveeCommand) {
    if (cmd.kind === 'help') {
      this.say(this.helpText());
      return;
    }
    if (cmd.kind === 'order-report') {
      this.startOrderReport('');
      return;
    }
    if (cmd.kind === 'qa-release') {
      this.startQaRelease('');
      return;
    }
    if (cmd.kind === 'switch-subscriber') {
      this.startSwitchSubscriber('');
      return;
    }
    if (cmd.kind === 'switch-admin') {
      this.confirmAction = 'switch-admin';
      this.say('Say yes to switch back to admin.');
      return;
    }
    if (cmd.kind === 'logout') {
      this.confirmAction = 'logout';
      this.say(cmd.reply);
      return;
    }
    if (cmd.kind === 'click') {
      this.clickByText(cmd.clickText || cmd.phrases[0]);
      return;
    }
    this.say(cmd.reply);
    this.router.navigateByUrl(cmd.path);
  }

  startOrderReport(subscriberName: string) {
    const role = this.getRole();
    if (role === 'subscriber' || role === 'admin-as-subscriber') {
      this.say('Opening the order report form.');
      this.router.navigateByUrl('/subscriber-reports/order-reports');
      return;
    }
    this.pendingAfterSubscriber = { kind: 'order-report', path: '/subscriber-reports/order-reports', speak: 'Opening the order report form.' };
    this.startSwitchSubscriber(subscriberName, 'Which subscriber should I order this report for?');
  }

  startQaRelease(query: string) {
    this.say(query ? 'Opening Quality Assurance and searching for ' + query + '.' : 'Opening Quality Assurance. Say a name or application id to search, then say click release.');
    this.router.navigateByUrl('/qa').then(() => {
      setTimeout(() => {
        if (query) {
          this.fillSearch(query);
        }
        this.clickByText('release from qa', true);
      }, 900);
    });
  }

  startSwitchSubscriber(name: string, prompt?: string) {
    this.awaitingSubscriber = true;
    this.subscriberQuery = name || '';
    this.loadSubscribers(() => {
      if (name) {
        const hits = this.filterSubscribers(name);
        if (hits.length === 1) {
          this.pickSubscriber(hits[0]);
          return;
        }
        if (hits.length === 0) {
          this.say('I could not find ' + name + '. Please say the subscriber name.');
          this.ping();
          return;
        }
        this.subscriberChoices = hits;
        this.say('I found a few matches. Pick one from the list, or say the name again.');
        this.ping();
        return;
      }
      this.say(prompt || 'Which subscriber should I switch to? Say the company name.');
      this.ping();
    });
  }

  pickSubscriber(user: any) {
    this.awaitingSubscriber = false;
    this.subscriberChoices = [];
    const pending = this.pendingAfterSubscriber || { kind: 'navigate', path: '/home' };
    this.pendingAfterSubscriber = null;
    sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
    this.say('Switching to ' + (user.companyName || 'that subscriber') + '.');
    const data = {
      id: user.id,
      role: 'admin',
      subscriberType: user.subscriberType
    };
    sessionStorage.setItem('subscriberLogin', JSON.stringify({
      Data: data,
      tableName: 'txnUsers',
      companyName: user.companyName
    }));
    setTimeout(() => {
      window.location.href = window.location.origin + window.location.pathname + '#/home';
      window.location.reload();
    }, 700);
  }

  consumePending() {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) {
      return;
    }
    sessionStorage.removeItem(PENDING_KEY);
    let pending: EveePending;
    try {
      pending = JSON.parse(raw);
    } catch (e) {
      return;
    }
    if (!pending) {
      return;
    }
    setTimeout(() => {
      if (pending.speak) {
        this.say(pending.speak);
      }
      if (pending.kind === 'order-report') {
        this.router.navigateByUrl('/subscriber-reports/order-reports');
        return;
      }
      if (pending.path) {
        this.router.navigateByUrl(pending.path).then(() => {
          setTimeout(() => {
            if (pending.query) {
              this.fillSearch(pending.query);
            }
            if (pending.clickText) {
              this.clickByText(pending.clickText, true);
            }
          }, 800);
        });
      }
    }, 400);
  }

  loadSubscribers(done?: () => void) {
    const token = this.dataService.getUserToken();
    const payLoad = {
      page: 1,
      limit: 10000,
      search: '',
      sortOrder: 'ASC',
      isDeleted: 0
    };
    this.dataService.getAdminUserDetails('admin/user/getAllSubscribers', token, payLoad).subscribe((res: any) => {
      const payload = res && (res.Data || res.data || {});
      const subscribers = payload && payload.subscribers ? payload.subscribers : [];
      this.subscriberChoices = Array.isArray(subscribers) ? subscribers.filter((v: any) => v.isDeleted == 0) : [];
      if (done) {
        done();
      }
      this.ping();
    }, () => {
      this.say('I could not load subscribers right now.');
      this.awaitingSubscriber = false;
    });
  }

  filterSubscribers(term: string): any[] {
    const q = this.normalize(term);
    return (this.subscriberChoices || []).filter((user: any) => {
      const company = this.normalize(user && user.companyName ? String(user.companyName) : '');
      const dvId = this.normalize(user && user.dvID ? String(user.dvID) : '');
      return company.indexOf(q) > -1 || dvId.indexOf(q) > -1;
    });
  }

  startListening() {
    const SpeechCtor = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;
    if (!SpeechCtor) {
      this.say('Voice is not supported in this browser. You can type instead.');
      return;
    }
    if (this.listening) {
      this.stopListening();
      return;
    }
    this.recognition = new SpeechCtor();
    this.recognition.lang = 'en-US';
    this.recognition.interimResults = false;
    this.recognition.maxAlternatives = 1;
    this.recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript;
      this.zone.run(() => this.handleTranscript(transcript));
    };
    this.recognition.onend = () => {
      this.zone.run(() => {
        this.listening = false;
        this.ping();
      });
    };
    this.recognition.onerror = () => {
      this.zone.run(() => {
        this.listening = false;
        this.ping();
      });
    };
    this.listening = true;
    this.recognition.start();
    this.ping();
  }

  stopListening() {
    this.listening = false;
    if (this.recognition) {
      try {
        this.recognition.stop();
      } catch (e) { }
    }
    this.ping();
  }

  private resolveSubscriberChoice(raw: string) {
    const hits = this.filterSubscribers(raw);
    if (hits.length === 1) {
      this.pickSubscriber(hits[0]);
      return;
    }
    if (hits.length > 1) {
      this.subscriberChoices = hits;
      this.say('I found a few matches. Pick one from the list.');
      return;
    }
    this.say('I could not find that subscriber. Try another name.');
  }

  private resolveConfirm(text: string) {
    const yes = /^(yes|yeah|yep|confirm|ok|okay|sure|do it)$/.test(text);
    const no = /^(no|nope|cancel|stop|never mind|nevermind)$/.test(text);
    const action = this.confirmAction;
    this.confirmAction = null;
    if (!yes && !no) {
      this.say('Please say yes or no.');
      this.confirmAction = action;
      return;
    }
    if (no) {
      this.say('Okay, cancelled.');
      return;
    }
    if (action === 'logout') {
      sessionStorage.clear();
      this.dataService.RemoveUserToken();
      this.say('Logging out.');
      this.router.navigate(['/login']);
      return;
    }
    if (action === 'switch-admin') {
      sessionStorage.removeItem('subscriberLogin');
      this.say('Switching back to admin.');
      setTimeout(() => {
        window.location.href = window.location.origin + window.location.pathname + '#/home';
        window.location.reload();
      }, 500);
    }
  }

  private matchCommand(text: string): EveeCommand {
    const role = this.getRole();
    const pool = this.commands.filter(cmd => cmd.roles.indexOf(role) > -1);
    let best: EveeCommand = null;
    let bestScore = 0;
    for (let i = 0; i < pool.length; i++) {
      const cmd = pool[i];
      for (let j = 0; j < cmd.phrases.length; j++) {
        const phrase = this.normalize(cmd.phrases[j]);
        if (!phrase) {
          continue;
        }
        if (text === phrase || text.indexOf(phrase) > -1 || phrase.indexOf(text) > -1) {
          const score = phrase.length;
          if (score > bestScore) {
            best = cmd;
            bestScore = score;
          }
        }
      }
    }
    return bestScore >= 4 ? best : null;
  }

  private isOrderIntent(text: string): boolean {
    return /order( a)? (report|screening)/.test(text) || text === 'order report' || text === 'new report';
  }

  private isReleaseIntent(text: string): boolean {
    return /release( a)? report/.test(text) || /release from qa/.test(text);
  }

  fillSearch(query: string) {
    const inputs = Array.prototype.slice.call(document.querySelectorAll('input.form-control, input[type="text"], input[type="search"]')) as HTMLInputElement[];
    const searchInput = inputs.find(el => {
      const ph = (el.placeholder || '').toLowerCase();
      const visible = !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
      return visible && (ph.indexOf('search') > -1 || el.className.indexOf('search') > -1);
    }) || inputs.find(el => !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length));
    if (!searchInput) {
      return false;
    }
    searchInput.focus();
    searchInput.value = query;
    searchInput.dispatchEvent(new Event('input', { bubbles: true }));
    searchInput.dispatchEvent(new Event('keyup', { bubbles: true }));
    searchInput.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  clickByText(label: string, quiet?: boolean): boolean {
    const want = this.normalize(label);
    const nodes = Array.prototype.slice.call(document.querySelectorAll('a, button, [role="button"], .nav-link, .dropdown-item, [title], [data-target], td, th, span, em, i, label')) as HTMLElement[];
    let best: HTMLElement = null;
    let bestScore = 0;
    for (let i = 0; i < nodes.length; i++) {
      const el = nodes[i];
      if (!el || !(el.offsetWidth || el.offsetHeight || el.getClientRects().length)) {
        continue;
      }
      if (this.isInsideEvee(el)) {
        continue;
      }
      const title = this.normalize(el.getAttribute('title') || el.getAttribute('aria-label') || '');
      const hay = this.normalize((el.innerText || el.textContent || '') + ' ' + title);
      if (title && (title === want || title.indexOf(want) > -1)) {
        best = el;
        bestScore = 2;
        break;
      }
      if (!hay) {
        continue;
      }
      if (hay === want || hay.indexOf(want) > -1) {
        const score = want.length / Math.max(hay.length, 1);
        if (score > bestScore) {
          best = el;
          bestScore = score;
        }
      }
    }
    if (best) {
      const clickable = best.closest('a, button, [role="button"], .nav-link, [data-target], [data-toggle]') as HTMLElement || best;
      clickable.click();
      if (!quiet) {
        this.say('Clicked ' + (clickable.innerText || label).trim() + '.');
      }
      return true;
    }
    if (!quiet) {
      this.say('I could not find "' + label + '" on this screen.');
    }
    return false;
  }

  helpText(): string {
    const role = this.getRole();
    if (role === 'admin') {
      return 'I can order a report after you pick a subscriber, open QA and release reports, switch subscribers, and open any admin page such as users, invoices, vendor orders, marketing, or settings. You can also say search, or click a button name.';
    }
    return 'I can order a report, open your reports, properties, users, payments, tenants, and settings. You can also say search, or click any button you see on the page.';
  }

  private stopSpeech() {
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
  }

  private primeVoices() {
    if (!window.speechSynthesis) {
      return;
    }
    this.chosenVoice = this.pickVoice();
    if (typeof window.speechSynthesis.addEventListener === 'function') {
      window.speechSynthesis.addEventListener('voiceschanged', () => {
        this.chosenVoice = this.pickVoice();
      });
    }
  }

  private pickVoice(): any {
    const voices = window.speechSynthesis.getVoices() || [];
    if (!voices.length) {
      return null;
    }
    const prefer = [
      'microsoft aria',
      'microsoft jenny',
      'microsoft zira',
      'google uk english female',
      'google us english female',
      'samantha',
      'victoria',
      'karen',
      'moira',
      'tessa',
      'fiona',
      'veena'
    ];
    const male = [
      'male', 'david', 'mark', 'guy', 'james', 'george', 'daniel', 'alex',
      'richard', 'ravi', 'sean', 'thomas', 'fred', 'jorge', 'diego'
    ];
    const scored = [];
    for (let i = 0; i < voices.length; i++) {
      const voice = voices[i];
      const name = (voice.name || '').toLowerCase();
      const lang = (voice.lang || '').toLowerCase();
      if (lang.indexOf('en') !== 0) {
        continue;
      }
      let isMale = false;
      for (let m = 0; m < male.length; m++) {
        if (name.indexOf(male[m]) > -1) {
          isMale = true;
          break;
        }
      }
      if (isMale) {
        continue;
      }
      let score = 1;
      for (let p = 0; p < prefer.length; p++) {
        if (name.indexOf(prefer[p]) > -1) {
          score = 100 - p;
          break;
        }
      }
      if (name.indexOf('female') > -1 || name.indexOf('woman') > -1 || name.indexOf('girl') > -1) {
        score += 20;
      }
      if (name.indexOf('neural') > -1 || name.indexOf('natural') > -1 || name.indexOf('online') > -1) {
        score += 8;
      }
      scored.push({ voice, score });
    }
    scored.sort((a, b) => b.score - a.score);
    if (scored.length) {
      return scored[0].voice;
    }
    return voices[0];
  }

  private speak(text: string) {
    this.stopSpeech();
    if (!window.speechSynthesis) {
      return;
    }
    const utter = new SpeechSynthesisUtterance(text);
    if (!this.chosenVoice) {
      this.chosenVoice = this.pickVoice();
    }
    if (this.chosenVoice) {
      utter.voice = this.chosenVoice;
    }
    utter.lang = (this.chosenVoice && this.chosenVoice.lang) || 'en-US';
    utter.rate = VOICE_RATE;
    utter.pitch = VOICE_PITCH;
    utter.volume = 1;
    this.speaking = true;
    utter.onend = () => {
      this.zone.run(() => {
        this.speaking = false;
        this.ping();
      });
    };
    window.speechSynthesis.speak(utter);
  }

  private normalize(value: string): string {
    return (value || '')
      .toLowerCase()
      .replace(/\b(evee|evie|eevee|ivy|eva)\b/g, ' ')
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private isInsideEvee(el: HTMLElement): boolean {
    return !!(el.closest && el.closest('.evee-root'));
  }

  private readSession(key: string): any {
    try {
      return JSON.parse(sessionStorage.getItem(key) || 'null');
    } catch (e) {
      return null;
    }
  }

  private ping() {
    if (this.onUpdate) {
      this.onUpdate();
    }
  }
}
